/**
 * Sign-in — against the real API, at last.
 *
 * `docs/05-admin/02` §2.1, one screen, three steps:
 *   1. **Credentials** — `POST /v1/auth/admin/login {email, password}` → `{mfa_token,
 *      totp_enrolment_required}`. The `mfa_token` is short-lived and single-purpose; it is not a
 *      session and it is never stored.
 *   2. **Two-factor** — `POST /v1/auth/admin/totp {mfa_token, totp_code}` → the token pair. A
 *      recovery code is accepted in the same field and consumed on use. If the login came back
 *      `totp_enrolment_required`, this step is **enrolment instead**: enrolment happens here and
 *      not on a screen of its own, because an admin who has not enrolled has no session yet — the
 *      short-lived `mfa_token` is the only thing they hold, and it is what enrolment authenticates
 *      on. There is nowhere else they could be sent (§2.1: "an unenrolled admin can go nowhere
 *      else").
 *   3. **Bootstrap** — `GET /v1/admin/me` hydrates the session, and the deep link the guard
 *      remembered is where you land.
 *
 * **The screen does not decide who you are.** It reports what the server said. In particular it
 * does not translate the server's deliberately uniform 401 into something more specific: the API
 * answers a wrong password and an unknown email identically so that an anonymous caller cannot
 * enumerate staff, and a client that helpfully distinguished them would undo that on the server's
 * behalf.
 *
 * There is no self-registration and no reset link. Admin accounts are invitation-only (arch-08 §3)
 * and recovery goes through a superadmin.
 */
import { useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Button, Icon } from '../components/primitives';
import { Input } from '../components/forms';
import { Stepper } from '../components/nav';
import { apiFetch, errorMessage } from '../lib/api/client';
import { tokenStore } from '../lib/auth/tokens';
import { adoptSession } from '../lib/auth/session';
import { useBotCheck } from '../lib/auth/turnstile';
import type { components } from '../api/generated/schema';
import type { ShellSearch } from '../app/Shell';

type LoginOut = components['schemas']['AdminLoginOut'];
type TokenOut = components['schemas']['AdminTokenOut'];

/**
 * `POST /v1/auth/admin/login` answers with **either** shape, and which one is the server telling
 * you whether a second factor is still required:
 *
 *   - `AdminLoginOut` — an `mfa_token`. The normal path: enrol or enter a code next.
 *   - `AdminTokenOut` — a session, minted from the password alone, because the deployment has
 *     `ADMIN_TOTP_REQUIRED=false`. Never possible in production, which forces the flag back on.
 *
 * Branching on the shape rather than on a flag the client keeps is the point: the console cannot
 * be configured into disagreeing with the server about whether MFA happened.
 */
export function isSession(out: LoginOut | TokenOut): out is TokenOut {
  return 'access_token' in out;
}
type EnrolStartOut = components['schemas']['TotpEnrolStartOut'];
type EnrolConfirmOut = components['schemas']['TotpEnrolConfirmOut'];

/**
 * A recovery code is *accepted in place of* an authenticator code, but it is **a different
 * endpoint** — `/v1/auth/admin/recovery` takes `{mfa_token, recovery_code}`. One field for both,
 * because that is what an operator has in front of them, and the shape decides where it goes: six
 * digits is a TOTP, anything else is a recovery code. Getting this wrong sends a recovery code to
 * `/totp` as `totp_code`, which fails in a way that reads like "my code is wrong".
 */
const SIX_DIGITS = /^\d{6}$/;

export function secondFactorEndpoint(entered: string): { path: string; field: 'totp_code' | 'recovery_code' } {
  return SIX_DIGITS.test(entered.trim())
    ? { path: '/v1/auth/admin/totp', field: 'totp_code' }
    : { path: '/v1/auth/admin/recovery', field: 'recovery_code' };
}

/** The shared secret, for anyone typing it into an authenticator by hand. */
export function secretFrom(otpauthUri: string): string | null {
  try {
    return new URL(otpauthUri).searchParams.get('secret');
  } catch {
    return null;
  }
}

const STEPS = [
  { label: 'Credentials', note: 'Email and password' },
  { label: 'Two-factor', note: 'Authenticator or recovery code' },
] as const;

export function Login() {
  const navigate = useNavigate();
  const { next, theme } = useSearch({ strict: false }) as ShellSearch;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [enrolUri, setEnrolUri] = useState<string | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<readonly string[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const bot = useBotCheck();

  async function submitCredentials() {
    setBusy(true);
    setError('');
    try {
      const out = await apiFetch<LoginOut | TokenOut>('/v1/auth/admin/login', {
        method: 'POST',
        /* The token only when there is one: a deployment with no bot check sends exactly the
           contract's two fields. */
        body: { email: email.trim(), password, ...(bot.token ? { turnstile_token: bot.token } : {}) },
      });

      if (isSession(out)) {
        /* Password-only deployment: the server has already decided no second factor is needed,
           so there is no step two to show. */
        tokenStore.write({ access: out.access_token, refresh: out.refresh_token });
        await adoptSession();
        navigate({ to: next ?? '/', search: { theme } as ShellSearch });
        return;
      }

      setMfaToken(out.mfa_token);
      if (out.totp_enrolment_required) {
        const started = await apiFetch<EnrolStartOut>('/v1/auth/admin/totp/enrol/start', {
          method: 'POST',
          body: { mfa_token: out.mfa_token },
        });
        setEnrolUri(started.otpauth_uri);
      }
    } catch (cause) {
      setError(errorMessage(cause));
      /* A token is single-use and this attempt spent it, right or wrong. */
      bot.reset();
    } finally {
      setBusy(false);
    }
  }

  async function submitCode() {
    /* v8 ignore next -- step two only renders once step one has set the token. */
    if (!mfaToken) return;
    setBusy(true);
    setError('');
    try {
      const { path, field } = secondFactorEndpoint(code);
      const tokens = await apiFetch<TokenOut>(path, {
        method: 'POST',
        body: { mfa_token: mfaToken, [field]: code.trim() },
      });
      tokenStore.write({ access: tokens.access_token, refresh: tokens.refresh_token });
      /* A token pair only exists for an enrolled admin — enrolment is a prerequisite of this call,
         not a consequence — so by here the session is a real one. `me` still decides *who*. */
      await adoptSession();
      navigate({ to: next ?? '/', search: { theme } as ShellSearch });
    } catch (cause) {
      /* The pair is discarded on any failure: a half-adopted session — tokens stored, identity
         unknown — is the state in which the console renders a shell around nobody. */
      tokenStore.clear();
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  async function confirmEnrolment() {
    /* v8 ignore next -- the enrolment step only renders once step one has set the token. */
    if (!mfaToken) return;
    setBusy(true);
    setError('');
    try {
      const out = await apiFetch<EnrolConfirmOut>('/v1/auth/admin/totp/enrol/confirm', {
        method: 'POST',
        body: { mfa_token: mfaToken, totp_code: code.trim() },
      });
      /* Shown exactly once — the server keeps only hashes. If they are lost from this screen they
         are gone, so the screen says so rather than trusting anyone to assume it. */
      setRecoveryCodes(out.recovery_codes);
      setEnrolUri(null);
      setCode('');
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  function restart() {
    setMfaToken(null);
    setEnrolUri(null);
    setRecoveryCodes(null);
    setCode('');
    setPassword('');
    setError('');
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        padding: 'var(--sp-lg)',
        background: 'var(--c-bg)',
      }}
    >
      <div style={{ display: 'grid', gap: 'var(--sp-lg)', width: '100%', maxWidth: 420 }}>
        <div style={{ display: 'grid', gap: 'var(--sp-sm)', justifyItems: 'center', textAlign: 'center' }}>
          <span
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 26,
              fontWeight: 'var(--fw-bold)',
              color: 'var(--c-ink)',
              letterSpacing: '-0.01em',
            }}
          >
            BookMyPandiit
          </span>
          <span className="kicker" style={{ color: 'var(--c-saffron-text)' }}>
            Admin console
          </span>
        </div>

        <div
          style={{
            display: 'grid',
            gap: 'var(--sp-md)',
            background: 'var(--c-surface)',
            border: '1px solid var(--c-border)',
            borderRadius: 'var(--r-card)',
            boxShadow: 'var(--sh-md)',
            padding: 'var(--sp-xxl)',
          }}
        >
          <Stepper steps={[...STEPS]} current={mfaToken ? 1 : 0} />

          {error && (
            <div
              role="alert"
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 'var(--sp-sm)',
                background: 'var(--c-error-bg)',
                color: 'var(--c-error-text)',
                border: '1px solid var(--c-error)',
                borderRadius: 'var(--r-tile)',
                padding: 'var(--sp-md)',
                fontSize: 'var(--fs-admin-cell)',
              }}
            >
              <Icon name="triangle-alert" size={16} />
              <span style={{ flex: 1 }}>{error}</span>
            </div>
          )}

          {recoveryCodes ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-sm)' }}>
                <Icon name="list-checks" size={18} style={{ color: 'var(--c-saffron)' }} />
                <span
                  style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--fs-admin-h2)', color: 'var(--c-ink)' }}
                >
                  Save your recovery codes
                </span>
              </div>
              <span style={{ fontSize: 'var(--fs-admin-cell)', color: 'var(--c-ink2)' }}>
                <strong style={{ fontWeight: 'var(--fw-semibold)' }}>Shown once, and never again.</strong> The
                server keeps only hashes of these, so nobody — including a superadmin — can read them back to you.
                Each works once, in place of an authenticator code, if you lose your phone.
              </span>
              <ol
                className="mono-num"
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                  gap: 'var(--sp-sm)',
                  margin: 0,
                  padding: 'var(--sp-md)',
                  listStylePosition: 'inside',
                  background: 'var(--c-sunk)',
                  border: '1px solid var(--c-border)',
                  borderRadius: 'var(--r-tile)',
                  fontSize: 'var(--fs-admin-cell)',
                  color: 'var(--c-ink)',
                }}
              >
                {recoveryCodes.map((c) => (
                  <li key={c}>{c}</li>
                ))}
              </ol>
              <Button size="lg" fullWidth onClick={restart}>
                I have saved them — sign in
              </Button>
            </>
          ) : enrolUri ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-sm)' }}>
                <Icon name="smartphone" size={18} style={{ color: 'var(--c-saffron)' }} />
                <span
                  style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--fs-admin-h2)', color: 'var(--c-ink)' }}
                >
                  Set up two-factor
                </span>
              </div>
              <span style={{ fontSize: 'var(--fs-admin-cell)', color: 'var(--c-ink2)' }}>
                Two-factor is mandatory for every admin and is set up at your first sign-in. Add this secret to your
                authenticator app as <strong style={{ fontWeight: 'var(--fw-semibold)' }}>BookMyPandiit Admin</strong>,
                then enter the code it shows.
              </span>
              <code
                style={{
                  display: 'block',
                  padding: 'var(--sp-md)',
                  background: 'var(--c-sunk)',
                  border: '1px solid var(--c-border)',
                  borderRadius: 'var(--r-tile)',
                  fontFamily: 'var(--font-mono)',
                  fontSize: 'var(--fs-admin-cell)',
                  color: 'var(--c-ink)',
                  wordBreak: 'break-all',
                }}
              >
                {secretFrom(enrolUri) ?? enrolUri}
              </code>
              <Input
                density="admin"
                label="Code from your authenticator"
                value={code}
                onChange={setCode}
                inputMode="numeric"
                placeholder="6 digits"
              />
              <Button size="lg" fullWidth disabled={busy} onClick={confirmEnrolment}>
                {busy ? 'Confirming…' : 'Confirm and continue'}
              </Button>
              <Button size="sm" variant="ghost" onClick={restart}>
                Back
              </Button>
            </>
          ) : mfaToken ? (
            <>
              <Input
                density="admin"
                label="Authenticator code"
                value={code}
                onChange={setCode}
                inputMode="numeric"
                placeholder="6 digits"
                icon="smartphone"
                helper="A recovery code works in its place, and is used up when you use it."
              />
              <Button size="lg" fullWidth disabled={busy} onClick={submitCode}>
                {busy ? 'Verifying…' : 'Verify and continue'}
              </Button>
              <Button size="sm" variant="ghost" onClick={restart}>
                Back
              </Button>
            </>
          ) : (
            <>
              <Input
                density="admin"
                label="Work email"
                value={email}
                onChange={setEmail}
                placeholder="you@bookmypandiit.com"
                icon="circle-user"
              />
              <Input
                density="admin"
                type="password"
                label="Password"
                value={password}
                onChange={setPassword}
              />
              {/* Takes no space unless Cloudflare asks for a click (interaction-only). */}
              <div ref={bot.container} />
              {bot.unavailable && (
                <span role="alert" style={{ fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-error-text)' }}>
                  The bot check would not load, and sign-in needs it. Check the connection or any
                  content blocker, then reload.
                </span>
              )}
              <Button
                size="lg"
                fullWidth
                disabled={busy || (bot.required && !bot.token)}
                onClick={submitCredentials}
              >
                {busy ? 'Checking…' : 'Continue'}
              </Button>
            </>
          )}

          <span style={{ fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-ink3)', textAlign: 'center' }}>
            Admin accounts are invitation-only. There is no self-registration and no reset link —
            recovery goes through a superadmin.
          </span>
        </div>
      </div>
    </div>
  );
}
